import { zodResolver } from '@hookform/resolvers/zod';
import { ForwardRefRenderFunction, forwardRef } from 'react';
import { FormProvider, useController, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import { FormActionButtons, ValidityPeriodForm } from '../../../common/Forms';
import { useDirtyFormBlockNavigation } from '../../../common/Forms/NavigationBlocker';
import { InputField } from '../../../common/Inputs';
import { FormColumn, FormRow } from '../../../common/LayoutComponents';
import { TerminalTypeDropdown } from '../../../StopRegistry/Components/TerminalTypeDropdown';
import { SelectTerminalMemberStopsDropdown } from '../../../StopRegistry/Terminals/Common';
import {
  TerminalFormState,
  terminalFormSchema,
} from '../../../StopRegistry/Terminals/TerminalDetails/Components/BasicDetails/Edit/schema';
import { TerminalNames } from './TerminalNames';

const testIds = {
  form: 'TerminalFormComponent::form',
  showHideButton: 'TerminalFormComponent::showHideButton',
  privateCode: 'TerminalFormComponent::privateCode',
  name: 'TerminalFormComponent::name',
  latitude: 'TerminalFormComponent::latitude',
  longitude: 'TerminalFormComponent::longitude',
  terminalType: 'TerminalFormComponent::terminalType',
  memberStops: 'TerminalLocationDetailsEdit::memberStops',
};

type TerminalFormProps = {
  readonly className?: string;
  readonly defaultValues:
    TerminalFormState | (() => Promise<TerminalFormState>);
  readonly onSubmit: (changes: TerminalFormState) => void;
  readonly onCancel: () => void;
  readonly testIdPrefix: string;
};

const TerminalFormComponent: ForwardRefRenderFunction<
  HTMLFormElement,
  TerminalFormProps
> = ({ className, defaultValues, onSubmit, onCancel, testIdPrefix }, ref) => {
  const { t } = useTranslation();

  const methods = useForm<TerminalFormState>({
    defaultValues,
    resolver: zodResolver(terminalFormSchema),
  });
  useDirtyFormBlockNavigation(methods.formState, 'TerminalForm', {
    allowSearchChange: true, // Allow search change so that moving the map does not show the navigation blocked dialog
    allowStateChange: true, // Allow state change so that location state updates do not show the navigation blocked dialog
  });
  const { handleSubmit } = methods;

  const {
    field: { value: selectedStops, onChange: onSelectedStopsChange },
  } = useController({
    name: 'selectedStops',
    control: methods.control,
    defaultValue: [],
  });

  return (
    <FormProvider {...methods}>
      <form
        data-testid={testIds.form}
        className={twMerge('flex flex-col', className)}
        onSubmit={handleSubmit(onSubmit)}
        ref={ref}
      >
        <div className="space-y-6 overflow-auto">
          <FormColumn className={twMerge('bg-background p-4', className)}>
            <div className="flex gap-4">
              <InputField<TerminalFormState>
                type="text"
                translationPrefix="terminal"
                fieldPath="privateCode"
                testId={testIds.privateCode}
                className="w-2/5"
                disabled
              />
              <InputField<TerminalFormState>
                type="text"
                translationPrefix="terminal"
                fieldPath="name"
                testId={testIds.name}
                className="w-full"
                required
              />
            </div>

            <FormRow>
              <TerminalNames />
            </FormRow>
          </FormColumn>
          <FormColumn>
            <FormRow
              mdColumns={4}
              className="px-4 sm:gap-x-4 md:gap-x-4 lg:gap-x-4"
            >
              <InputField<TerminalFormState>
                type="number"
                translationPrefix="map"
                fieldPath="latitude"
                testId={testIds.latitude}
                step="any"
              />
              <InputField<TerminalFormState>
                type="number"
                translationPrefix="map"
                fieldPath="longitude"
                testId={testIds.longitude}
                step="any"
              />

              <InputField<TerminalFormState>
                translationPrefix="terminalDetails.basicDetails"
                fieldPath="terminalType"
                testId={testIds.terminalType}
                className="md:col-span-2"

                inputElementRenderer={(props) => (
                  <TerminalTypeDropdown {...props} />
                )}
              />
            </FormRow>
            <FormRow
              mdColumns={1}
              className="px-4 sm:gap-x-4 md:gap-x-4 lg:gap-x-4"
            >
              <div>
                <div className="mb-2 text-sm font-bold">
                  {t(($) => $.terminalDetails.location.memberStopsTotal, {
                    total: selectedStops.length,
                  })}
                </div>
                <SelectTerminalMemberStopsDropdown
                  value={selectedStops}
                  onChange={onSelectedStopsChange}
                  testId={testIds.memberStops}
                />
              </div>
            </FormRow>
            <FormRow className="border-t border-light-grey p-4">
              <ValidityPeriodForm dateInputRowClassName="sm:gap-x-4 md:gap-x-4 lg:gap-x-4" />
            </FormRow>
          </FormColumn>
        </div>
        <FormActionButtons
          onCancel={onCancel}
          testIdPrefix={testIdPrefix}
          isDisabled={
            !methods.formState.isDirty || methods.formState.isSubmitting
          }
          isSubmitting={methods.formState.isSubmitting}
          variant="modal"
        />
      </form>
    </FormProvider>
  );
};

export const TerminalForm = forwardRef(TerminalFormComponent);
